// src/funciones/cargarEvaluacionEstudiante.js

/*
==========================================================
YUTAKE

Cargar evaluación de un estudiante

Replica EXACTAMENTE el flujo de HomeV2:

  evidencias (progreso_temas)
    -> motorEvaluacion.resolverEstadoAcademico
    -> motorYuta.crearMotorYuta
    -> tutor (lo que ve el estudiante en su dashboard)

Además devuelve el desglose por tema y componente que
expone el motor oficial.

Uso mixto: hoy la usa la vista del profesor. Más adelante
puede usarla HomeV2 y Mis Metas para garantizar el mismo
dato en toda la app.

No escribe en Firestore. No crea motores nuevos.
==========================================================
*/

import {
    collection,
    doc,
    getDoc,
    getDocs,
    query,
    where
} from "firebase/firestore";

import { db } from "../firebase/firebaseConfig";

import motorEvaluacion from "./motores/motorEvaluacion";
import motorYuta from "./motores/motorYuta";

const CODIGO_EXAMEN = {
    1: "icfes",
    2: "unal",
    3: "udea"
};

/*
----------------------------------------------------------
CATÁLOGOS ACADÉMICOS

Son iguales para todos los estudiantes, así que se
descargan una sola vez por sesión. Al cambiar de
estudiante solo se consultan sus datos propios
(importante con conexiones lentas).
----------------------------------------------------------
*/

let cacheCatalogos = null;

const cargarCatalogos = async () => {

    if (cacheCatalogos) {
        return cacheCatalogos;
    }

    const [
        carrerasSnap,
        temaComponenteSnap,
        materiasTemasSnap
    ] = await Promise.all([
        getDocs(collection(db, "carreras")),
        getDocs(collection(db, "tema_componente")),
        getDocs(collection(db, "materias_temas"))
    ]);

    cacheCatalogos = {
        carreras:
            carrerasSnap.docs.map(d => d.data()),
        temaComponente:
            temaComponenteSnap.docs.map(d => ({ id: d.id, ...d.data() })),
        materiasTemas:
            materiasTemasSnap.docs.map(d => ({ id: d.id, ...d.data() }))
    };

    return cacheCatalogos;
};

const aFecha = (valor) => {

    if (!valor) return null;

    if (typeof valor.toDate === "function") {
        return valor.toDate();
    }

    const fecha = new Date(valor);

    return isNaN(fecha.getTime()) ? null : fecha;
};

/*
----------------------------------------------------------
FUNCIÓN PRINCIPAL

uid        -> estudiante a evaluar
fechaCorte -> opcional. Si se envía, solo se usan las
              evidencias hasta esa fecha (para reconstruir
              la evolución en el tiempo, objetivo 2).
----------------------------------------------------------
*/

const cargarEvaluacionEstudiante = async (
    uid,
    { fechaCorte = null } = {}
) => {

    if (!uid) {
        throw new Error("Falta el identificador del estudiante.");
    }

    await motorEvaluacion.inicializarMotor();

    // Datos del usuario (igual que HomeV2)

    const usuarioSnap = await getDoc(doc(db, "usuarios", uid));

    if (!usuarioSnap.exists()) {
        return null;
    }

    const usuario = usuarioSnap.data();

    const codigoExamen = CODIGO_EXAMEN[usuario.examenPrincipal];

    // Solo los documentos de este estudiante: mismo resultado
    // que HomeV2 (que descarga todo y filtra), con menos datos.

    const [
        progresoTemasSnap,
        progresoMetaSnap,
        catalogos
    ] = await Promise.all([
        getDocs(
            query(
                collection(db, "progreso_temas"),
                where("userId", "==", uid)
            )
        ),
        codigoExamen
            ? getDoc(doc(db, "progreso_metas", `${uid}_${codigoExamen}`))
            : Promise.resolve(null),
        cargarCatalogos()
    ]);

    // Evidencias (igual que HomeV2)

    let evidencias = [];

    progresoTemasSnap.docs.forEach(documento => {
        evidencias.push(...(documento.data().evidencias || []));
    });

    if (fechaCorte) {

        const corte = aFecha(fechaCorte);

        if (corte) {
            evidencias = evidencias.filter(evidencia => {
                const fecha = aFecha(evidencia.fecha);
                return fecha && fecha.getTime() <= corte.getTime();
            });
        }
    }

    const progresoMeta =
        progresoMetaSnap && progresoMetaSnap.exists()
            ? progresoMetaSnap.data()
            : null;

    // Carreras del examen principal (igual que HomeV2)

    const carreras = catalogos.carreras.filter(
        c => Number(c.ExamenID) === Number(usuario.examenPrincipal)
    );

    const carreraActual = carreras.find(
        c => Number(c.ID) === Number(usuario.carreraObjetivo)
    );

    // Motor oficial (mismos parámetros que HomeV2)

    const nodo = motorEvaluacion.crearNodoAcademico({
        tipo: "examen",
        id: "UNAL",
        hijos: []
    });

    const estadoAcademico = motorEvaluacion.resolverEstadoAcademico({
        nodo,
        evidencias
    });

    // Interpretación oficial (mismo contexto que HomeV2)

    const contextoYuta = {
        progreso: progresoMeta,
        carreras,
        componentes: [],
        temas: catalogos.temaComponente,
        materiasTemas: catalogos.materiasTemas,
        curvas: [],
        examenes: [],
        metas: []
    };

    const yuta = motorYuta.crearMotorYuta({
        estadoAcademico,
        usuario,
        objetivo: carreraActual,
        contexto: contextoYuta,
        nombreExamen: "UNAL",
        nombreObjetivo: carreraActual?.Carrera ?? "Carrera"
    });

    // Objeto tutor (idéntico al que HomeV2 entrega a Dashboard)

    const tutor = {
        ...yuta.dashboard,
        ...yuta.resumenSesion,
        carreraObjetivo: {
            ...yuta.dashboard.carreraObjetivo,
            nombre:
                carreraActual?.Carrera ??
                yuta.dashboard.carreraObjetivo.nombre,
            universidad:
                carreraActual?.Universidad ??
                yuta.dashboard.carreraObjetivo.universidad,
            ciudad:
                carreraActual?.Ciudad ??
                yuta.dashboard.carreraObjetivo.ciudad
        },
        puntajeObjetivo:
            carreraActual?.PuntajeCorte ??
            yuta.dashboard.puntajeObjetivo
    };

    return {
        usuario,
        progresoMeta,
        carreraActual,
        evidencias,
        estadoAcademico,
        desglose: estadoAcademico.desglose ?? null,
        tutor
    };
};

export default cargarEvaluacionEstudiante;